import { Spinner } from '@recipes/ui';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/lib/auth/auth-provider';

function FullScreenLoading() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner className="text-muted-foreground" />
    </div>
  );
}

export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoading />;
  if (status === 'anonymous')
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

export function RequireAdmin() {
  const { status, user } = useAuth();
  if (status === 'loading') return <FullScreenLoading />;
  // The role SUPPORT has one place in the admin area: every other address leads there.
  if (user?.role === 'SUPPORT') return <Navigate to="/admin/support" replace />;
  if (user?.role !== 'ADMIN') return <Navigate to="/" replace />;
  return <Outlet />;
}

/** The support inbox: roles ADMIN and SUPPORT. */
export function RequireSupportStaff() {
  const { status, user } = useAuth();
  if (status === 'loading') return <FullScreenLoading />;
  if (user?.role !== 'ADMIN' && user?.role !== 'SUPPORT') return <Navigate to="/" replace />;
  return <Outlet />;
}

export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoading />;
  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }
  return <>{children}</>;
}
